import React, { useEffect, useRef, useMemo, type ComponentType } from 'react';
import { expVal } from '@atlassian/jira-feature-experiments';
import { ff } from '@atlassian/jira-feature-flagging-using-meta';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl';
import { useIssueKey } from '@atlassian/jira-issue-context-service';
import { type ActivitySortOrderType, NEWEST_FIRST } from '@atlassian/jira-issue-shared-types';
import Button from '@atlassian/jira-issue-view-common/src/component/button';
import {
	ContextualAnalyticsData,
	SCREEN,
	FireScreenAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { FormatMessage } from '@atlassian/jira-shared-types';
import type { HistoryItem } from '../../types';
import HistoryItemComponent from './history-item';
import messages from './messages';

const HISTORY_GROUPED_ITEMS_CATEGORY = 'HISTORY' as const;

export type Props = {
	isLoadingMore: boolean;
	hasMore: boolean;
	history: HistoryItem[];
	sortOrder: ActivitySortOrderType;
	onLoadMore: () => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	GroupedActivityEntryComponent?: ComponentType<Record<any, any>>;
};

export const LoadMoreButton = ({
	formatMessage,
	isLoadingMore,
	onLoadMore,
	sortOrder,
}: {
	formatMessage: FormatMessage;
	isLoadingMore: boolean;
	sortOrder: ActivitySortOrderType;
	onLoadMore: () => void;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const prevIsLoadingMore = useRef(isLoadingMore);

	useEffect(() => {
		prevIsLoadingMore.current = isLoadingMore;
	}, [isLoadingMore]);

	const onLoadMoreButtonClick = () => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});
		fireUIAnalytics(analyticsEvent, 'loadMoreButtonClicked', {
			loadMoreButtonTypeClicked:
				sortOrder === NEWEST_FIRST ? 'LOAD_OLDER_BUTTON' : 'LOAD_NEWER_BUTTON',
		});
		onLoadMore();
	};

	return (
		<Button
			shouldFitContainer
			isDisabled={isLoadingMore}
			isLoading={isLoadingMore}
			onClick={onLoadMoreButtonClick}
		>
			{formatMessage(messages.loadMore)}
		</Button>
	);
};

// To be cleaned up when activity experiment closes: jira-issue-view-activity-feed-syntax-value-updates: https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/issue_view_activity_timeline/setup
export const HISTORY_ACTIVITY_REPEAT_USAGE_CACHE_KEY = 'history_activity_repeat_usage_cache_value';

const HistoryItems = ({
	history,
	isLoadingMore,
	onLoadMore,
	hasMore,
	sortOrder,
	GroupedActivityEntryComponent: GroupedActivityEntry,
}: Props) => {
	const intl = useIntl();

	// To be cleaned up when activity experiment closes: jira-issue-view-activity-feed-syntax-value-updates: https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/issue_view_activity_timeline/setup
	const issueKey = useIssueKey();
	let isRepeatedUseOfHistoryActivityFeedAcrossIssues = false;
	useMemo(() => {
		if (ff('jira-issue-view-activity-feed-temp-metrics_hpdjz')) {
			const issueKeyFromStorage = window.localStorage?.getItem(
				HISTORY_ACTIVITY_REPEAT_USAGE_CACHE_KEY,
			);
			if (issueKeyFromStorage) {
				if (issueKeyFromStorage !== issueKey) {
					// eslint-disable-next-line react-hooks/exhaustive-deps
					isRepeatedUseOfHistoryActivityFeedAcrossIssues = true;
				}
			} else {
				window.localStorage.setItem(HISTORY_ACTIVITY_REPEAT_USAGE_CACHE_KEY, issueKey);
			}
		}
	}, []);

	// if (!GroupedActivityEntry && fg('issue_view_activity_timeline_targeting_gate_')) {
	// 	// @todo: fire analytic error here
	// }

	return (
		<ContextualAnalyticsData sourceName="historyItems" sourceType={SCREEN}>
			{history.map((historyItem, index) =>
				historyItem?.type === HISTORY_GROUPED_ITEMS_CATEGORY &&
				GroupedActivityEntry &&
				expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false) ? (
					<GroupedActivityEntry key={index} item={historyItem} />
				) : (
					<HistoryItemComponent key={index} historyItem={historyItem} />
				),
			)}
			{hasMore && (
				<div data-testid="issue-history.ui.history-items.load-more-button">
					<LoadMoreButton
						formatMessage={intl.formatMessage}
						isLoadingMore={isLoadingMore}
						onLoadMore={onLoadMore}
						sortOrder={sortOrder}
					/>
				</div>
			)}
			{
				// To be cleaned up when activity experiment closes: jira-issue-view-activity-feed-syntax-value-updates: https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/issue_view_activity_timeline/setup
				ff('jira-issue-view-activity-feed-temp-metrics_hpdjz') ? (
					<FireScreenAnalytics attributes={{ isRepeatedUseOfHistoryActivityFeedAcrossIssues }} />
				) : (
					<FireScreenAnalytics />
				)
			}
		</ContextualAnalyticsData>
	);
};

export default HistoryItems;
